// @flow
import React, { useCallback } from 'react';
import classNames from 'classnames/bind';

import Tooltip from 'app/common/ui/Tooltip';
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';
import { DotsLoader } from 'app/common/components/Loader';
import { useCurrencySign } from 'app/common/components-next/currency';
import PlainIcon, { iconTypes, colorTypes } from 'app/common/ui-next/plain-icon';

import type { DishApiType } from '../../MenuDishesApi';
import type { OrderDataItemType } from '../OrderData';
import styles from './Dish.module.scss';;


const cx = classNames.bind(styles);

type Props = {
  orderFormOpened: boolean,
  balanceLeft?: number,
  addToOrder: (item: OrderDataItemType) => void,
  isAdded: boolean,
  loading: boolean,
  ...$Exact<DishApiType>,
}

const Dish = ({
  orderFormOpened,
  balanceLeft = 0,
  addToOrder,
  isAdded,
  loading,
  available,
  cost,
  title,
  image,
  portion,
  guid,
}: Props) => {
  const currency = useCurrencySign();

  const notEnough = orderFormOpened && (balanceLeft - cost < 0);
  const notEnabled = orderFormOpened && !available;
  const locked = notEnabled || notEnough;
  const reasonText = notEnabled ? 'Недоступно для заказа' : 'Недостаточно средств';
  const reason = !isAdded && locked ? reasonText : '';
  const addedToOrder = orderFormOpened && isAdded;
  const notAvailable = locked || loading;

  // добавление в заказ
  const handleAddToOrder = useCallback((e) => {
    e.preventDefault();
    // если уже добавлено, форма не открыта или не доступно добавление для этого товара,
    // то просто выходим
    if (addedToOrder || !orderFormOpened || notAvailable) return;

    addToOrder({
      guid,
      n: 1,
      cost,
      type: 0,
      title,
      portion,
      free_for_sale: false,
    });
  }, [notAvailable, addedToOrder, orderFormOpened, guid, cost, title, portion, addToOrder]);

  return (
    <Tooltip text={reason}>
      <div // eslint-disable-line jsx-a11y/click-events-have-key-events
        className={cx(styles.root, { notAvailable, addedToOrder })}
        onClick={handleAddToOrder}
      >
        <div className={cx({ notAvailableImage: notAvailable })}>
          {!image
            ? <PlainIcon
              width="40px"
              height="auto"
              type={iconTypes.DISH}
              colorType={colorTypes.COLOR_PRIMARY}
            />
            : <img alt="" src={image} />}
        </div>
        <div className={styles.main}>
          <div className={cx(styles.title, { notAvailable })}>
            {!reason && !notAvailable
              ? <Tooltip text={title}>{title}</Tooltip>
              : title}
          </div>
          <div className={styles.info}>
            {(portion || typeof portion === 'number')
              && <div className={cx(styles.portion, { notAvailable })}>
                <PlainIcon
                  type={iconTypes.PORTION}
                  colorType={colorTypes.COLOR_DARKGRAY}
                />
                &nbsp;
                {portion}
              </div>}
            <div className={cx(styles.cost, { notAvailable })}>
              {toFixedValueCurrency(cost, false, currency)}
            </div>
          </div>
        </div>
        {loading && <DotsLoader className={styles.dotsLoader} />}
      </div>
    </Tooltip>
  );
};

export default Dish;
